import { Link } from "react-router-dom";
import { useUiLang } from "../../stores/uiLang";

function AcceptableUsePolicyPageEn() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Acceptable Use Policy</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Guidelines for appropriate use of Project Explore by all users,
          including teachers, administrators, and students.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="text-xs text-gray-500">
          <strong>Operator:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Effective Date:</strong> May 27, 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Scope</h2>
          <p className="mb-3">
            This Acceptable Use Policy ("AUP") applies to all users of the
            Project Explore platform, including district administrators, school
            administrators, teachers, and students. By accessing or using
            Project Explore, you agree to this AUP.
          </p>
          <p className="mb-3">
            This AUP supplements and does not replace the contracting district's
            own codes of conduct, technology use policies, or student handbooks.
            Where a district policy is more restrictive than this AUP, the
            district policy controls for users in that district.
          </p>
          <p>
            Parents or legal guardians accessing student records through Project
            Explore's parental access path are bound by the access, security,
            and confidentiality provisions of this AUP with respect to those
            records.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">2. Permitted Use</h2>
          <p className="mb-3">
            Project Explore is licensed exclusively for educational use as
            authorized by the contracting district or school. Permitted uses
            include:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Accessing and completing assigned curriculum, video content, and assessments</li>
            <li>Teachers creating class rosters, assigning content, and reviewing student progress</li>
            <li>Administrators provisioning accounts and managing licenses within the terms of the district's agreement</li>
            <li>Exporting student data and reports for educational reporting purposes</li>
          </ul>
          <p>
            <strong>AI-generated content.</strong> AI-generated feedback and
            content within Project Explore is provided for educational purposes
            within the contracted district. Users may not extract, redistribute,
            or use such content outside the educational context for which it was
            generated.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">3. Prohibited Use</h2>
          <p className="mb-3">All users are prohibited from:</p>

          <p className="font-medium mb-1">Account and Access</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Sharing login credentials with any other person</li>
            <li>Accessing, or attempting to access, another user's account or data</li>
            <li>Using another person's identity or account</li>
          </ul>

          <p className="font-medium mb-1">Platform Integrity</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Uploading or transmitting malware, viruses, or any harmful code</li>
            <li>Attempting to reverse-engineer, decompile, or extract platform source code</li>
            <li>Bypassing, disabling, or circumventing access controls, row-level security policies, or authentication mechanisms</li>
            <li>Probing the platform for vulnerabilities without written authorization from Startup Smartup</li>
          </ul>

          <p className="font-medium mb-1">Conduct</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Using the platform to harass, bully, threaten, or harm other users</li>
            <li>Uploading or submitting content that violates copyright, defamation, privacy, or any applicable law</li>
            <li>Submitting personally identifiable information about individuals other than yourself</li>
            <li>Using the platform for commercial purposes outside the district's educational license</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">4. Teacher-Specific Responsibilities</h2>
          <p className="mb-2">Teachers are responsible for:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Providing appropriate supervision of student use during class sessions</li>
            <li>Not sharing student data, progress reports, or assessment results with parties who do not have a legitimate educational interest as defined by FERPA</li>
            <li>Not granting platform access to individuals who are not enrolled in the teacher's class or who have not been authorized by the school administrator</li>
            <li>Reporting suspected account compromises or inappropriate student submissions to the school administrator</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">5. Student-Specific Responsibilities</h2>
          <p className="mb-2">Students are expected to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Use the platform only for assigned educational activities</li>
            <li>Treat all classmates with respect; do not submit content intended to demean or embarrass another student</li>
            <li>Follow teacher and administrator direction regarding platform use</li>
            <li>Report any technical issues, suspected errors, or concerns to their teacher</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">6. Enforcement</h2>
          <p className="mb-3">Violations of this AUP may result in:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Warning issued by a school or district administrator</li>
            <li>Temporary suspension of account access</li>
            <li>Permanent termination of account access</li>
            <li>Referral to the contracting district for disciplinary action consistent with the district's student code of conduct</li>
            <li>In cases involving potential illegal activity, referral to appropriate law enforcement</li>
          </ul>
          <p>
            Startup Smartup reserves the right to suspend or terminate any
            account that is, in its reasonable judgment, in violation of this
            AUP, applicable law, or the terms of the district's agreement.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">7. Cooperation with District Investigations</h2>
          <p>
            Startup Smartup will cooperate with district investigations of AUP
            violations, including providing access to relevant activity logs and
            records as permitted under FERPA and the district's data processing
            agreement.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">8. Reporting Violations</h2>
          <p className="mb-2">To report suspected violations of this AUP, contact:</p>
          <p className="mb-3">
            <strong>Startup Smartup — Support</strong>
            <br />
            <a href="mailto:support@startupsmartup.com" className="text-indigo-600 underline">
              support@startupsmartup.com
            </a>
          </p>
          <p>
            Reports from students should be directed to the teacher or school
            administrator first.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">9. Changes to This Policy</h2>
          <p>
            Material changes to this AUP will be announced through the platform
            with at least 30 days' notice before taking effect. Continued use
            of the platform after the effective date of a revised AUP
            constitutes acceptance.
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Effective Date: May 27, 2026
          <br />
          Cross-reference:{" "}
          <Link to="/terms" className="text-indigo-600 underline">Terms of Service</Link>{" | "}
          <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link>
        </div>
      </section>
    </div>
  );
}

function AcceptableUsePolicyPageEs() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Política de uso aceptable</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Pautas para el uso adecuado de Project Explore por parte de todos los
          usuarios, incluidos maestros, administradores y estudiantes.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
          Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.
        </p>
        <p className="text-xs text-gray-500">
          <strong>Operador:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Fecha de entrada en vigor:</strong> 27 de mayo de 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Alcance</h2>
          <p className="mb-3">
            Esta Política de uso aceptable ("PUA") se aplica a todos los
            usuarios de la plataforma Project Explore, incluidos los
            administradores del distrito, los administradores escolares, los
            maestros y los estudiantes. Al entrar a Project Explore o usarlo,
            aceptas esta PUA.
          </p>
          <p className="mb-3">
            Esta PUA complementa, y no reemplaza, los códigos de conducta, las
            políticas de uso de tecnología o los manuales para estudiantes del
            propio distrito contratante. Cuando una política del distrito sea
            más estricta que esta PUA, la política del distrito prevalece para
            los usuarios de ese distrito.
          </p>
          <p>
            Los padres o tutores legales que entren a los registros de los
            estudiantes por la vía de acceso para padres de Project Explore
            quedan sujetos a las disposiciones de esta PUA sobre acceso,
            seguridad y confidencialidad respecto de esos registros.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">2. Uso permitido</h2>
          <p className="mb-3">
            Project Explore se licencia exclusivamente para uso educativo,
            según lo autorice el distrito o la escuela contratante. Los usos
            permitidos incluyen:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Entrar al currículo asignado, a los videos y a las evaluaciones, y completarlos</li>
            <li>Que los maestros creen listas de clase, asignen contenido y revisen el progreso de los estudiantes</li>
            <li>Que los administradores creen cuentas y administren licencias dentro de los términos del acuerdo del distrito</li>
            <li>Exportar datos e informes de estudiantes para fines de reportes educativos</li>
          </ul>
          <p>
            <strong>Contenido generado por IA.</strong> Los comentarios y el
            contenido generados por IA dentro de Project Explore se ofrecen
            con fines educativos dentro del distrito contratado. Los usuarios
            no pueden extraer ni redistribuir ese contenido, ni usarlo fuera
            del contexto educativo para el que se generó.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">3. Uso prohibido</h2>
          <p className="mb-3">Se prohíbe a todos los usuarios:</p>

          <p className="font-medium mb-1">Cuenta y acceso</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Compartir sus datos de inicio de sesión con otra persona</li>
            <li>Entrar, o intentar entrar, a la cuenta o a los datos de otro usuario</li>
            <li>Usar la identidad o la cuenta de otra persona</li>
          </ul>

          <p className="font-medium mb-1">Integridad de la plataforma</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-4">
            <li>Subir o transmitir malware, virus o cualquier código dañino</li>
            <li>Intentar aplicar ingeniería inversa, descompilar o extraer el código fuente de la plataforma</li>
            <li>Evitar, desactivar o eludir los controles de acceso, las políticas de seguridad a nivel de fila o los mecanismos de autenticación</li>
            <li>Buscar vulnerabilidades en la plataforma sin autorización por escrito de Startup Smartup</li>
          </ul>

          <p className="font-medium mb-1">Conducta</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Usar la plataforma para acosar, intimidar, amenazar o dañar a otros usuarios</li>
            <li>Subir o entregar contenido que infrinja derechos de autor, constituya difamación, viole la privacidad o infrinja cualquier ley aplicable</li>
            <li>Entregar información de identificación personal de personas que no sean uno mismo</li>
            <li>Usar la plataforma con fines comerciales fuera de la licencia educativa del distrito</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">4. Responsabilidades específicas de los maestros</h2>
          <p className="mb-2">Los maestros son responsables de:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Supervisar de forma adecuada el uso que hacen los estudiantes durante las sesiones de clase</li>
            <li>No compartir datos de estudiantes, informes de progreso ni resultados de evaluaciones con personas que no tengan un interés educativo legítimo, según lo define FERPA</li>
            <li>No dar acceso a la plataforma a personas que no estén inscritas en la clase del maestro o que el administrador escolar no haya autorizado</li>
            <li>Informar al administrador escolar sobre sospechas de cuentas comprometidas o trabajos inapropiados de los estudiantes</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">5. Responsabilidades específicas de los estudiantes</h2>
          <p className="mb-2">Se espera que los estudiantes:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Usen la plataforma solo para las actividades educativas asignadas</li>
            <li>Traten con respeto a todos sus compañeros; no entreguen contenido que busque humillar o avergonzar a otro estudiante</li>
            <li>Sigan las indicaciones de los maestros y administradores sobre el uso de la plataforma</li>
            <li>Informen a su maestro sobre cualquier problema técnico, posible error o inquietud</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">6. Aplicación de la política</h2>
          <p className="mb-3">Las infracciones de esta PUA pueden dar lugar a:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Una advertencia de un administrador de la escuela o del distrito</li>
            <li>Suspensión temporal del acceso a la cuenta</li>
            <li>Terminación permanente del acceso a la cuenta</li>
            <li>Remisión al distrito contratante para una medida disciplinaria acorde con el código de conducta estudiantil del distrito</li>
            <li>En casos que impliquen una posible actividad ilegal, remisión a las autoridades correspondientes</li>
          </ul>
          <p>
            Startup Smartup se reserva el derecho de suspender o cancelar
            cualquier cuenta que, a su juicio razonable, infrinja esta PUA, la
            ley aplicable o los términos del acuerdo del distrito.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">7. Cooperación con las investigaciones del distrito</h2>
          <p>
            Startup Smartup cooperará con las investigaciones del distrito
            sobre infracciones de la PUA, incluido dar acceso a los registros
            de actividad pertinentes, según lo permitan FERPA y el acuerdo de
            procesamiento de datos del distrito.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">8. Cómo reportar infracciones</h2>
          <p className="mb-2">Para reportar posibles infracciones de esta PUA, comunícate con:</p>
          <p className="mb-3">
            <strong>Startup Smartup: Soporte</strong>
            <br />
            <a href="mailto:support@startupsmartup.com" className="text-indigo-600 underline">
              support@startupsmartup.com
            </a>
          </p>
          <p>
            Los reportes de los estudiantes deben dirigirse primero al maestro
            o al administrador escolar.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">9. Cambios a esta política</h2>
          <p>
            Los cambios importantes a esta PUA se anunciarán en la plataforma
            con al menos 30 días de aviso antes de que entren en vigor. Seguir
            usando la plataforma después de la fecha de entrada en vigor de una
            PUA revisada significa que la aceptas.
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Fecha de entrada en vigor: 27 de mayo de 2026
          <br />
          Referencias cruzadas:{" "}
          <Link to="/terms" className="text-indigo-600 underline">Términos del servicio</Link>{" | "}
          <Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link>
        </div>
      </section>
    </div>
  );
}

export default function AcceptableUsePolicyPage() {
  return useUiLang() === "es" ? <AcceptableUsePolicyPageEs /> : <AcceptableUsePolicyPageEn />;
}
